import { useEffect, useLayoutEffect, useRef, useState } from "react";
import SectionTitle from "../ui/SectionTitle";
import ReviewCard from "../ui/ReviewCard";
import { logoWatermarkItems } from "../../data/logoWatermark";

const AUTOPLAY_MS = 4000;
// The list is rendered three times; we always settle back into the middle copy,
// so scrolling past either end looks like an endless loop.
const COPIES = 3;

function shuffle(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function getSlides(track) {
  return track.querySelectorAll(".testimonials-carousel-slide");
}

function isStartAligned(slide) {
  return getComputedStyle(slide).scrollSnapAlign.includes("start");
}

// Desktop snaps cards to the start (three visible), mobile to the center.
function slideScrollLeft(track, slides, index) {
  const slide = slides[index];
  return isStartAligned(slide)
    ? slide.offsetLeft - slides[0].offsetLeft
    : slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
}

function currentSlideIndex(track, slides) {
  let best = 0;
  let bestDist = Infinity;
  slides.forEach((_, index) => {
    const dist = Math.abs(slideScrollLeft(track, slides, index) - track.scrollLeft);
    if (dist < bestDist) {
      bestDist = dist;
      best = index;
    }
  });
  return best;
}

function ArrowIcon({ direction }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={direction < 0 ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TestimonialsSection({ t, reviews }) {
  const trackRef = useRef(null);
  const hoverRef = useRef(false);
  const touchingRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const [items] = useState(() => shuffle(reviews));
  const [activeIndex, setActiveIndex] = useState(0);
  const count = items.length;

  // Start in the middle copy.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || count === 0) return;
    const slides = getSlides(track);
    track.scrollTo({ left: slideScrollLeft(track, slides, count), behavior: "instant" });
  }, [count]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || count === 0) return undefined;
    let settleTimer;

    // Once scrolling stops in an outer copy, jump to the same card in the middle copy.
    function recenter() {
      if (touchingRef.current) return;
      const slides = getSlides(track);
      const index = currentSlideIndex(track, slides);
      if (index < count || index >= count * 2) {
        const middle = (index % count) + count;
        track.scrollTo({ left: slideScrollLeft(track, slides, middle), behavior: "instant" });
      }
    }

    function handleScroll() {
      setActiveIndex(currentSlideIndex(track, getSlides(track)) % count);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(recenter, 150);
    }

    function handleTouchStart() {
      touchingRef.current = true;
    }

    function handleTouchEnd() {
      touchingRef.current = false;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(recenter, 400);
    }

    track.addEventListener("scroll", handleScroll, { passive: true });
    track.addEventListener("touchstart", handleTouchStart, { passive: true });
    track.addEventListener("touchend", handleTouchEnd, { passive: true });
    track.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      window.clearTimeout(settleTimer);
      track.removeEventListener("scroll", handleScroll);
      track.removeEventListener("touchstart", handleTouchStart);
      track.removeEventListener("touchend", handleTouchEnd);
      track.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [count]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const interval = window.setInterval(() => {
      const track = trackRef.current;
      if (!track || document.hidden || hoverRef.current || touchingRef.current) return;
      // Give people time to read after they swiped or clicked.
      if (Date.now() - lastInteractionRef.current < AUTOPLAY_MS * 1.5) return;
      scrollBySlide(track, 1);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(interval);
  }, []);

  function markInteraction() {
    lastInteractionRef.current = Date.now();
  }

  function scrollBySlide(track, direction) {
    const slides = getSlides(track);
    const target = currentSlideIndex(track, slides) + direction;
    track.scrollTo({ left: slideScrollLeft(track, slides, target), behavior: "smooth" });
  }

  function step(direction) {
    markInteraction();
    if (trackRef.current) scrollBySlide(trackRef.current, direction);
  }

  function goToSlide(index) {
    markInteraction();
    const track = trackRef.current;
    if (!track) return;
    const slides = getSlides(track);
    track.scrollTo({ left: slideScrollLeft(track, slides, count + index), behavior: "smooth" });
    setActiveIndex(index);
  }

  const loopItems = Array.from({ length: COPIES }, (_, copy) =>
    items.map((review) => ({ review, copy })),
  ).flat();

  return (
    <section className="landing-section testimonials-section">
      <div className="testimonials-logo-scatter" aria-hidden="true">
        {logoWatermarkItems.map((item, index) => (
          <img
            key={index}
            src="/img/mizulogo.png"
            alt=""
            className="testimonials-logo-scatter-item"
            style={{
              top: item.top,
              left: item.left,
              width: `${item.size}px`,
              transform: `translate(-50%, -50%) rotate(${item.rotate}deg)`,
            }}
          />
        ))}
      </div>

      <div
        className="container testimonials-section-inner"
        onMouseEnter={() => {
          hoverRef.current = true;
        }}
        onMouseLeave={() => {
          hoverRef.current = false;
        }}
        onFocus={markInteraction}
        onTouchStart={markInteraction}
        onWheel={markInteraction}
      >
        <SectionTitle label={t.title} variant="light" />

        <div
          className="testimonials-grid testimonials-carousel"
          ref={trackRef}
        >
          {loopItems.map(({ review, copy }) => {
            const text = t.items[review.id];
            const isClone = copy !== 1;
            return (
              <div
                key={`${copy}-${review.id}`}
                className="testimonials-carousel-slide"
                aria-hidden={isClone || undefined}
                inert={isClone || undefined}
              >
                <ReviewCard
                  name={text.name}
                  text={text.text}
                  rating={review.rating}
                  source={review.source}
                  sourceLabel={t.googleBadge}
                />
              </div>
            );
          })}
        </div>

        <div className="testimonials-carousel-controls">
          <button
            type="button"
            className="testimonials-carousel-arrow"
            onClick={() => step(-1)}
            aria-label={t.prev}
          >
            <ArrowIcon direction={-1} />
          </button>

          <div
            className="testimonials-carousel-dots"
            role="tablist"
            aria-label={t.title}
          >
            {items.map((review, index) => (
              <button
                key={review.id}
                type="button"
                role="tab"
                aria-selected={activeIndex === index}
                className={
                  activeIndex === index
                    ? "testimonials-carousel-dot is-active"
                    : "testimonials-carousel-dot"
                }
                onClick={() => goToSlide(index)}
              >
                <span className="visually-hidden">
                  {t.items[review.id].name}
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="testimonials-carousel-arrow"
            onClick={() => step(1)}
            aria-label={t.next}
          >
            <ArrowIcon direction={1} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
